import React, { useEffect, useMemo, useState } from "react";

import {
  CButton,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
  CSpinner
} from "@coreui/react";
import PropTypes from "prop-types";
import { toastr } from "react-redux-toastr";

import URL from "../../config/api";
import axios from "../../config/axios";
import "./styles.css";

/* "Editar acessos" do cadastro do grupo: quais telas do menu cada perfil vê e
   abre NESTE grupo. A grade vem do backend (access/group/acessos/{id}) já com
   o padrão de cada caixa; o que difere do padrão fica destacado. Salvar manda
   só as caixas mexidas — o backend decide se cada uma vira personalizada ou
   volta a seguir o padrão. */

const chave = (menuId, roleId) => `${menuId}:${roleId}`;

const normalizar = texto =>
  String(texto || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

function AcessosDoGrupo({ show, onClose, groupId, groupName, token }) {
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [perfis, setPerfis] = useState([]);
  const [telas, setTelas] = useState([]);
  const [alteracoes, setAlteracoes] = useState({});
  const [busca, setBusca] = useState("");
  const [soPersonalizadas, setSoPersonalizadas] = useState(false);

  const headers = { authorization: `Bearer ${token}` };

  const aplicarResposta = dados => {
    setPerfis(dados.perfis || []);
    setTelas(dados.telas || []);
    setAlteracoes({});
  };

  useEffect(() => {
    if (!show || !groupId) return;
    setCarregando(true);
    axios
      .get(`${URL.api}access/group/acessos/${groupId}`, { headers })
      .then(resp => {
        if (resp.data.success) aplicarResposta(resp.data.data);
        else
          toastr.error(
            "Erro",
            resp.data.error || "Não foi possível carregar os acessos."
          );
      })
      .catch(() =>
        toastr.error("Erro", "Não foi possível carregar os acessos.")
      )
      .finally(() => setCarregando(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, groupId]);

  // Valor que a caixa mostra: a alteração ainda não salva, ou o que veio do backend.
  const valor = (tela, roleId) => {
    const k = chave(tela.menu_id, roleId);
    return k in alteracoes ? alteracoes[k] : tela.perfis[roleId].permitido;
  };

  const marcar = (tela, roleId, permitido) => {
    const k = chave(tela.menu_id, roleId);
    setAlteracoes(old => {
      const novo = { ...old };
      // Voltou ao que está salvo: não é mais uma alteração.
      if (permitido === tela.perfis[roleId].permitido) delete novo[k];
      else novo[k] = permitido;
      return novo;
    });
  };

  const restaurarTela = tela => {
    perfis.forEach(perfil =>
      marcar(tela, perfil.id, tela.perfis[perfil.id].padrao)
    );
  };

  const difereDoPadrao = tela =>
    perfis.some(
      perfil => valor(tela, perfil.id) !== tela.perfis[perfil.id].padrao
    );

  const secoes = useMemo(() => {
    const termo = normalizar(busca.trim());
    const filtradas = telas.filter(tela => {
      if (termo && !normalizar(`${tela.secao} ${tela.nome}`).includes(termo))
        return false;
      if (soPersonalizadas && !difereDoPadrao(tela)) return false;
      return true;
    });
    const porSecao = [];
    filtradas.forEach(tela => {
      const ultima = porSecao[porSecao.length - 1];
      if (ultima && ultima.nome === tela.secao) ultima.telas.push(tela);
      else porSecao.push({ nome: tela.secao, telas: [tela] });
    });
    return porSecao;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [telas, busca, soPersonalizadas, alteracoes, perfis]);

  const totalAlteracoes = Object.keys(alteracoes).length;

  const salvar = async () => {
    const lista = Object.entries(alteracoes).map(([k, permitido]) => {
      const [menuId, roleId] = k.split(":").map(Number);
      return { menu_id: menuId, role_id: roleId, permitido };
    });
    if (!lista.length) return;

    setSalvando(true);
    try {
      const resp = await axios.post(
        `${URL.api}access/group/acessos/${groupId}`,
        { alteracoes: lista },
        { headers }
      );
      if (resp.data.success) {
        aplicarResposta(resp.data.data);
        toastr.success(
          "Acessos salvos",
          "Os usuários do grupo veem a mudança no próximo login."
        );
      } else {
        toastr.error(
          "Erro",
          resp.data.error || "Não foi possível salvar os acessos."
        );
      }
    } catch (error) {
      toastr.error(
        "Erro",
        error.response?.data?.error || "Não foi possível salvar os acessos."
      );
    } finally {
      setSalvando(false);
    }
  };

  const restaurarGrupo = async () => {
    // eslint-disable-next-line no-alert
    const ok = window.confirm(
      "Voltar TODAS as telas deste grupo ao padrão? As personalizações serão perdidas."
    );
    if (!ok) return;

    setSalvando(true);
    try {
      const resp = await axios.post(
        `${URL.api}access/group/acessos/${groupId}`,
        { restaurar: true },
        { headers }
      );
      if (resp.data.success) {
        aplicarResposta(resp.data.data);
        toastr.success("Padrão restaurado", "O grupo voltou ao acesso padrão.");
      } else {
        toastr.error("Erro", resp.data.error || "Não foi possível restaurar.");
      }
    } catch (error) {
      toastr.error("Erro", "Não foi possível restaurar o padrão.");
    } finally {
      setSalvando(false);
    }
  };

  const fechar = () => {
    if (totalAlteracoes > 0) {
      // eslint-disable-next-line no-alert
      const ok = window.confirm(
        "Há alterações não salvas. Fechar mesmo assim?"
      );
      if (!ok) return;
    }
    onClose();
  };

  return (
    <CModal show={show} onClose={fechar} size="xl" className="acessos-grupo">
      <CModalHeader closeButton>
        <CModalTitle>
          Acessos do grupo{groupName ? ` — ${groupName}` : ""}
        </CModalTitle>
      </CModalHeader>
      <CModalBody>
        <p className="acessos-grupo__ajuda">
          Marque quais telas cada perfil vê no menu e abre neste grupo. Liberar
          uma tela libera o cadastro inteiro dela (listar, criar, editar e
          excluir), sempre dentro do nível do usuário. As caixas{" "}
          <span className="acessos-grupo__amostra">destacadas</span> estão
          diferentes do padrão.
        </p>

        <div className="acessos-grupo__filtros">
          <input
            type="search"
            className="form-control"
            placeholder="Buscar tela ou seção"
            value={busca}
            onChange={e => setBusca(e.target.value)}
          />
          <label className="acessos-grupo__check-filtro">
            <input
              type="checkbox"
              checked={soPersonalizadas}
              onChange={e => setSoPersonalizadas(e.target.checked)}
            />{" "}
            Só as diferentes do padrão
          </label>
        </div>

        {carregando ? (
          <div className="text-center py-5">
            <CSpinner color="primary" />
          </div>
        ) : (
          <div className="acessos-grupo__tabela table-responsive">
            <table className="table table-sm mb-0">
              <thead>
                <tr>
                  <th scope="col" className="acessos-grupo__col-tela">
                    Tela
                  </th>
                  {perfis.map(perfil => (
                    <th
                      key={perfil.id}
                      scope="col"
                      className="text-center"
                      title={perfil.name}
                    >
                      {perfil.name}
                    </th>
                  ))}
                  <th scope="col">
                    <span className="sr-only">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {secoes.map(secao => (
                  <React.Fragment key={secao.nome}>
                    <tr className="acessos-grupo__secao">
                      <th colSpan={perfis.length + 2} scope="colgroup">
                        {secao.nome}
                      </th>
                    </tr>
                    {secao.telas.map(tela => (
                      <tr key={tela.menu_id}>
                        <th scope="row" className="acessos-grupo__col-tela">
                          {tela.nome}
                          {tela.url && (
                            <small className="d-block">{tela.url}</small>
                          )}
                        </th>
                        {perfis.map(perfil => {
                          const marcado = valor(tela, perfil.id);
                          const padrao = tela.perfis[perfil.id].padrao;
                          const mexida =
                            chave(tela.menu_id, perfil.id) in alteracoes;
                          return (
                            <td
                              key={perfil.id}
                              className={[
                                "text-center",
                                marcado !== padrao
                                  ? "acessos-grupo__diferente"
                                  : "",
                                mexida ? "acessos-grupo__mexida" : ""
                              ].join(" ")}
                            >
                              <input
                                type="checkbox"
                                checked={marcado}
                                onChange={e =>
                                  marcar(tela, perfil.id, e.target.checked)
                                }
                                aria-label={`${perfil.name} acessa ${tela.nome}`}
                                title={`Padrão: ${
                                  padrao ? "acessa" : "não acessa"
                                }`}
                              />
                            </td>
                          );
                        })}
                        <td className="text-right">
                          {difereDoPadrao(tela) && (
                            <CButton
                              size="sm"
                              color="link"
                              onClick={() => restaurarTela(tela)}
                              title="Voltar esta tela ao padrão"
                            >
                              Padrão
                            </CButton>
                          )}
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
                {!secoes.length && (
                  <tr>
                    <td
                      colSpan={perfis.length + 2}
                      className="text-center text-muted py-4"
                    >
                      Nenhuma tela encontrada.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </CModalBody>
      <CModalFooter className="acessos-grupo__rodape">
        <CButton
          color="secondary"
          variant="outline"
          onClick={restaurarGrupo}
          disabled={salvando || carregando}
        >
          Restaurar padrão do grupo
        </CButton>
        <div className="acessos-grupo__acoes">
          <CButton color="secondary" onClick={fechar} disabled={salvando}>
            Fechar
          </CButton>
          <CButton
            color="primary"
            onClick={salvar}
            disabled={salvando || !totalAlteracoes}
          >
            {salvando ? (
              <CSpinner size="sm" />
            ) : (
              `Salvar${totalAlteracoes ? ` (${totalAlteracoes})` : ""}`
            )}
          </CButton>
        </div>
      </CModalFooter>
    </CModal>
  );
}

AcessosDoGrupo.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  groupId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  groupName: PropTypes.string,
  token: PropTypes.string.isRequired
};

AcessosDoGrupo.defaultProps = {
  groupName: ""
};

export default AcessosDoGrupo;
